import { Component, computed, inject } from '@angular/core'
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatTableModule } from '@angular/material/table'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DEV_OR_LOCALHOST } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import { LayerPersonalPayload, LayerStandard } from '@core/models/layers.model'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { LayersService } from '@core/services/layers.service'
import { SharedService } from '@core/services/share.service'
import { ToasterService } from '@core/services/toaster.service'
import { validateGeojsonFile } from '@shared/others/geojson-file-validator'
import { multipleEmailsValidator } from '@shared/others/multiple-email-validator'
import { BehaviorSubject } from 'rxjs'
import { LayerSharedConfirmDialogComponent } from './layer-shared-confirm-dialog/layer-shared-confirm-dialog.component'

@Component({
  selector: 'app-dialog-personal-layers',
  templateUrl: './dialog-personal-layers.component.html',
  styleUrls: ['./dialog-personal-layers.component.scss'],
  providers: [HttpService],
  imports: [
    //Forms
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,

    // Material
    MatTooltipModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinner,
  ],
})
export class DialogPersonalLayersComponent {
  readonly dialogRef = inject(MatDialogRef<DialogPersonalLayersComponent>)
  devOrLocalhost = inject<BehaviorSubject<string>>(DEV_OR_LOCALHOST)
  uploadToEdit = inject(MAT_DIALOG_DATA)
  private readonly _dialog = inject(MatDialog)
  private readonly _uploadedLayersService = inject(UploadedLayersService)
  private readonly _layerService = inject(LayersService)
  private readonly _shareService = inject(SharedService)
  private readonly _toasterService = inject(ToasterService)

  file2Up: File | null = null // in case of new
  fileName: string = '' // in case of edit (file not loaded only the reference)
  isUploading: boolean = false // Temporary until api do this async
  geojsonError: string | null = null // set when the selected .geojson file is not a usable FeatureCollection

  personalLayerForm = new FormGroup({
    file: new FormControl([Validators.required]),
    type: new FormControl({ value: this.uploadToEdit.layerName, disabled: this.uploadToEdit.id }, [
      Validators.required,
    ]),
    emails: new FormControl(this.uploadToEdit.shared_with, [multipleEmailsValidator()]),
  })

  layers$ = this._layerService.layers$.asReadonly()
  groupedLayers$ = computed(() => {
    const groupsMap = new Map<string, LayerStandard[]>()
    const layers = this.layers$()

    for (const layer of layers) {
      if (!groupsMap.has(layer.category)) {
        groupsMap.set(layer.category, [])
      }
      groupsMap.get(layer.category)!.push(layer)
    }

    const result: Array<{ category: string; layers: LayerStandard[] }> = []
    for (const [category, layersInCategory] of groupsMap.entries()) {
      result.push({ category, layers: layersInCategory })
    }
    return result
  })

  localhostErrorMessage = "Layers can't be shared when the API (backend) is locally hosted"
  emailListToolTip =
    'Write email addresses separated by a semicolon ` ; ` to make this layer accessible to other users. Removing a user from the list will remove their access to the layer.'

  ngOnInit() {
    if (this.uploadToEdit) {
      this.fileName = this.uploadToEdit.name
    }
  }

  async getFileOnLoad(event: Event) {
    const target = event.target as HTMLInputElement
    if (target.files && target.files.length > 0) {
      this.file2Up = target.files[0]
      this.fileName = target.files[0].name
      this.geojsonError = null

      if (this.fileName.toLowerCase().endsWith('.geojson')) {
        const result = await validateGeojsonFile(this.file2Up)
        if (!result.valid) {
          this.geojsonError = result.error ?? 'This file is not a valid GeoJSON layer.'
        }
      }
    }
  }

  async submitFormPersonalLayer() {
    const emailsFormControl = this.personalLayerForm.get('emails') as FormControl<
      string | null
    > | null
    const emailsList = this._shareService.convertEmailsToList(emailsFormControl)

    if (!this.uploadToEdit.id) {
      // New personal layer
      if (emailsList.length == 0) {
        this._uploadPersonalLayer(emailsList)
        return
      }

      const dialogRef = this._dialog.open(LayerSharedConfirmDialogComponent, {
        data: { isNew: true, filename: this.file2Up?.name },
      })

      dialogRef.afterClosed().subscribe((hasValidateSharing) => {
        if (hasValidateSharing) {
          this._uploadPersonalLayer(emailsList)
        }
      })
    } else {
      // Update personal layer
      const dialogRef = this._dialog.open(LayerSharedConfirmDialogComponent, {
        data: { isNew: false, filename: this.uploadToEdit.name },
      })
      dialogRef.afterClosed().subscribe((hasValidateSharing) => {
        if (hasValidateSharing) this._updateListShare(emailsList)
      })
    }
  }

  cancel(): void {
    this.dialogRef.close(false)
  }

  private _uploadPersonalLayer(emails: string[]) {
    if (this.geojsonError) {
      alert(`This GeoJSON file cannot be uploaded: ${this.geojsonError}`)
      return
    }

    const layerType = this.personalLayerForm.get('type')?.value
    const file = this.file2Up

    if (file && file.size && file.size > 20 * 1024 * 1024) {
      alert('Uploaded files cannot be bigger than 20 MB')
      return
    }

    const spaceState = this._uploadedLayersService.stillSpaceOnAccountState$()
    if (spaceState.status !== 'success' || !spaceState.data) {
      alert("You don't have enough space to upload a fill.")
      return
    }

    if (!this.uploadToEdit.id && layerType !== 'Geojson') {
      if (file && file.size && file.size / 1000 >= 800) {
        //Ask user to confirm when uploading potential large files.
        if (
          !confirm(
            'Your file may cover a large area, and may take up to some minutes to generate. Would you like to continue anyway?',
          )
        )
          return
      }
    }

    const foundLayer = this.layers$().find((l) => l.name === layerType)
    let layerTypePayload: LayerPersonalPayload
    //@todo remove this work around when GeoJSON layers are implemented as a Type
    if (layerType == 'Geojson') {
      layerTypePayload = {
        workspacename: 'Geojson',
        layer_type: 'Geojson',
      }
    } else if (foundLayer) {
      layerTypePayload = {
        workspacename: foundLayer.workspacename,
        layer_type: foundLayer.layer_type,
      }
    } else {
      return
    }

    this.isUploading = true

    if (this.file2Up) {
      this._uploadedLayersService
        .addPersonalLayer(this.file2Up, layerTypePayload, emails)
        .then(() => {
          this.isUploading = false
          this.dialogRef.close(true)
        })
    }
  }

  private async _updateListShare(emails: string[]) {
    this.isUploading = true
    await this._uploadedLayersService.updatePersonalLayer(this.uploadToEdit.id, emails)
    this.isUploading = false
    this.dialogRef.close(true)
  }

  emailListTooltipMessage() {
    if (this.devOrLocalhost.getValue() == 'Localhost') {
      return this.localhostErrorMessage
    }
    return this.emailListToolTip
  }

  toaster() {
    this._toasterService.openSnackBar('gjdflsgjdfogjdfogidjfgoidfjgofijio')
  }
}
